import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Tracks People Search bulk unlock batches (POST .../workflows/bulk and
 * .../phone-workflows/bulk).
 *
 * The bulk endpoints fan out server-side into one sub-workflow per contact,
 * and every one of their SSE events carries the aggregate batch's own id as
 * `bulkBatchId` - the same mechanism Workspace's bulk email/phone runs use.
 * What those events do NOT carry is any contact identity (see WorkflowEvent),
 * and the bulk create response has no per-item workflowId either, so there is
 * no way to attribute a given event to a specific row without one GET per
 * workflow - which for a 25-row batch is 50 requests to learn what one search
 * refetch already tells us.
 *
 * So this deliberately only counts finishes per batch, exactly like
 * workspaceEmailSlice's emailBulkTick: the count drives "is this batch done",
 * and the rows themselves are filled in by refetching the search (see
 * useBulkContactUnlock.ts). Per-row spinners come from the contactIds
 * recorded here, not from per-row workflow state.
 */
export interface RadarBulkBatch {
  /**
   * Client-side key, assigned when the user clicks - NOT the server's batch
   * id, which doesn't exist yet at that point.
   *
   * Batches are keyed on this so progress can be shown from the moment of the
   * click. Waiting for the create response instead made the bar appear
   * already-finished on large batches: the service enqueues the work before
   * it replies, so for 100 contacts the phone waterfall had ~90 of them done
   * by the time the response landed, and the bar mounted at 92/100 rather
   * than counting up. Same temporary-key-then-swap approach.
   */
  key: string;
  /** Filled in once the create call returns - until then there's nothing to poll. */
  bulkBatchId: string | null;
  category: 'email' | 'phone';
  /** The contacts this batch was started for - what the spinners and the post-refetch reveal are keyed on. */
  contactIds: string[];
  /** Expected number of finished contacts (one per item accepted). */
  total: number;
  /** counts.completed from the batch's own doc - authoritative, but only as fresh as the last poll. */
  serverCompleted: number;
  /** Set once the batch is known finished, so progress can show total/total rather than stalling just short of it. */
  done: boolean;
}

export interface RadarBulkUnlockState {
  batches: Record<string, RadarBulkBatch>;
  /**
   * `${personId}:${field}` -> that contact's own workflowId within a batch,
   * learned straight off its SSE event's `input.personId` (Workflow-Service
   * commit 78166cb puts `input: {personId, companyId, docId, columnName}` on
   * every pushed event). Free - no per-row GET - and it's what lets the
   * existing per-row cells read a bulk row's live result exactly like a
   * single-click unlock. See useContactUnlock.resolveFieldWorkflowId.
   */
  workflowIdByPersonField: Record<string, string>;
  /**
   * Contacts submitted to a bulk unlock, recorded BEFORE the create call is
   * made. Workflow-Service enqueues a batch's jobs before its HTTP response
   * returns, so the quickest items finish before the browser knows the
   * bulkBatchId - matching on personId as well means those events are still
   * recognised as ours instead of being dropped (which left their rows
   * spinning until the whole batch, slow email included, was done).
   */
  pendingContactIds: Record<string, true>;
  /**
   * `${personId}:${field}` for contacts whose waterfall stopped because the
   * account ran out of credit, taken from the finish event's own
   * `status: "credit_exhausted"` (Workflow-Service keeps this distinct from
   * "not_found" precisely so a caller can tell "nothing exists for this
   * contact" from "we stopped searching"). Lets a row say so instead of
   * reporting a miss it never actually looked for.
   */
  creditExhaustedByPersonField: Record<string, true>;
  /**
   * Contacts of an All-tab export over 100, which runs in the background like
   * a large bulk unlock: no pills, progress in the notification panel. Their
   * finish events are how the export's server-created batches get found and
   * shown there (see useRadarBulkUnlockEvents). An entry drops once both its
   * email and phone finishes are in.
   */
  backgroundExportContactIds: Record<string, true>;
}

const initialState: RadarBulkUnlockState = {
  batches: {},
  workflowIdByPersonField: {},
  pendingContactIds: {},
  creditExhaustedByPersonField: {},
  backgroundExportContactIds: {},
};

const radarBulkUnlockSlice = createSlice({
  name: 'radarBulkUnlock',
  initialState,
  reducers: {
    /** Opens a batch at click time, before the create call is even made - see RadarBulkBatch.key. */
    radarBulkRunStarted(
      state,
      action: PayloadAction<{
        key: string;
        category: 'email' | 'phone';
        contactIds: string[];
        total: number;
      }>
    ) {
      const { key, category, contactIds, total } = action.payload;
      state.batches[key] = {
        key,
        bulkBatchId: null,
        category,
        contactIds,
        total,
        serverCompleted: 0,
        done: false,
      };
    },

    /** Attaches the server's batch id once the create call returns. */
    radarBulkBatchIdAssigned(state, action: PayloadAction<{ key: string; bulkBatchId: string }>) {
      const batch = state.batches[action.payload.key];
      if (batch) batch.bulkBatchId = action.payload.bulkBatchId;
    },

    /**
     * Applies the batch doc's own counters from a poll. `completed` is the
     * authoritative count; `done` snaps progress to total, and is set both
     * when the doc reports completedAt and when it has already been swept
     * (a 404 only happens 30s after completion - see useBulkContactUnlock).
     */
    radarBulkBatchSynced(
      state,
      action: PayloadAction<{ key: string; completed?: number; done?: boolean }>
    ) {
      const batch = state.batches[action.payload.key];
      if (!batch) return;
      if (typeof action.payload.completed === 'number') {
        batch.serverCompleted = Math.max(batch.serverCompleted, action.payload.completed);
      }
      if (action.payload.done) batch.done = true;
    },

    /** Marks contacts as about to be submitted - see pendingContactIds. */
    radarBulkPendingRegistered(state, action: PayloadAction<string[]>) {
      action.payload.forEach((id) => {
        state.pendingContactIds[id] = true;
      });
    },

    radarBulkPendingCleared(state, action: PayloadAction<string[]>) {
      action.payload.forEach((id) => {
        delete state.pendingContactIds[id];
      });
    },

    /** Records a background export's contacts - see backgroundExportContactIds. */
    radarBackgroundExportRegistered(state, action: PayloadAction<string[]>) {
      action.payload.forEach((id) => {
        state.backgroundExportContactIds[id] = true;
      });
    },

    radarBackgroundExportCleared(state, action: PayloadAction<string[]>) {
      action.payload.forEach((id) => {
        delete state.backgroundExportContactIds[id];
      });
    },

    /**
     * Applies a `bulk.progress` SSE event, matched on the SERVER's batch id.
     *
     * The aggregate event carries no contact identity at all - just
     * `completed`/`total` for the whole batch - so it can only be matched this
     * way, which means it is ignored until the create call has returned and
     * radarBulkBatchIdAssigned has run. That's fine: it feeds `serverCompleted`,
     * which is only ever the floor under the per-contact derivation in
     * radarBulkBatchProgress (see its note on why deriving beats accumulating).
     *
     * What this buys is the 5-second poll no longer being the only thing
     * moving the bar - the batch reports itself every 5 completions.
     */
    radarBulkProgressReceived(
      state,
      action: PayloadAction<{ bulkBatchId: string; completed: number; total: number; isFinal: boolean }>
    ) {
      const { bulkBatchId, completed, total, isFinal } = action.payload;
      for (const batch of Object.values(state.batches)) {
        if (batch.bulkBatchId !== bulkBatchId) continue;
        batch.serverCompleted = Math.max(batch.serverCompleted, completed);
        // The server's `total` is accepted items (post-dedup), which can be
        // lower than the contacts we submitted - trust it over our own count,
        // or the bar can never reach its end.
        if (total > 0) batch.total = Math.min(batch.total, total);
        if (isFinal) batch.done = true;
        return;
      }
    },

    /** Records which workflow belongs to which contact, from the event's own `input.personId`. */
    radarBulkWorkflowMapped(
      state,
      action: PayloadAction<{
        personId: string;
        field: 'email' | 'phone';
        workflowId: string;
        /** The finish event's `status === "credit_exhausted"` - see creditExhaustedByPersonField. */
        creditExhausted?: boolean;
      }>
    ) {
      const { personId, field, workflowId, creditExhausted } = action.payload;
      if (!personId) return;
      state.workflowIdByPersonField[`${personId}:${field}`] = workflowId;
      if (creditExhausted) state.creditExhaustedByPersonField[`${personId}:${field}`] = true;
      // A background export always unlocks both, so the contact is done with
      // once both of its finishes are in.
      if (
        state.backgroundExportContactIds[personId] &&
        state.workflowIdByPersonField[`${personId}:email`] &&
        state.workflowIdByPersonField[`${personId}:phone`]
      ) {
        delete state.backgroundExportContactIds[personId];
      }
    },

    /**
     * Forgets these contacts' bulk workflow mappings, so a finished bulk run no
     * longer counts their fields as revealed. Used when they're deleted from
     * Saved - the server drops the unlock along with the saved record.
     */
    radarBulkContactsForgotten(state, action: PayloadAction<string[]>) {
      action.payload.forEach((id) => {
        (['email', 'phone'] as const).forEach((field) => {
          delete state.workflowIdByPersonField[`${id}:${field}`];
          delete state.creditExhaustedByPersonField[`${id}:${field}`];
        });
      });
    },

    radarBulkBatchCleared(state, action: PayloadAction<string>) {
      delete state.batches[action.payload];
    },
  },
});

/**
 * Whether any bulk unlock is in flight - keeps the bulk action bar mounted
 * (and its inline progress visible) after the selection is cleared, which
 * happens as soon as the batch is kicked off. Same arrangement as
 * `isBulkProcessing`, read straight from the store here since
 * the counts already live in Redux.
 */
export const selectIsRadarBulkUnlocking = (state: { radarBulkUnlock: RadarBulkUnlockState }) =>
  Object.keys(state.radarBulkUnlock.batches).length > 0;

/**
 * Which of the given kinds already have a batch running - lets a new run be
 * gated per kind rather than treating every bulk unlock as one shared
 * resource. Email and phone run on independent bulk endpoints and queues, so
 * an email run in progress has no reason to block a phone-only click; only a
 * kind that's genuinely already in flight should be refused.
 */
export const selectRunningRadarBulkKinds = (
  state: { radarBulkUnlock: RadarBulkUnlockState },
  kinds: ('email' | 'phone')[]
): ('email' | 'phone')[] => {
  const running = new Set(Object.values(state.radarBulkUnlock.batches).map((b) => b.category));
  return kinds.filter((k) => running.has(k));
};

/** The per-workflow completion state a batch's progress is derived from. */
export interface RadarBulkCompletionLookup {
  workflowIdByPersonField: Record<string, string>;
  email: Record<string, { completedAt: string | null } | undefined>;
  phone: Record<string, { completedAt: string | null } | undefined>;
}

/**
 * How many of a batch's contacts are finished.
 *
 * DERIVED by walking the batch's own contactIds and asking whether each one's
 * workflow has completed - never accumulated from events. An event counter
 * cannot self-correct: one missed event (the service enqueues a batch's jobs
 * before its create response returns, so the quickest items finish before the
 * client knows the batch exists) offsets it permanently, which is what parked
 * a 100-contact batch at 94/100. Recomputing is also immune to duplicate or
 * replayed events. Same approach as own bulk counters, which
 * recompute from contactIds for exactly these reasons.
 *
 * `serverCompleted` is the floor, covering contacts whose events never
 * arrived at all, and `done` pins it to total once the batch is known
 * finished.
 */
export function radarBulkBatchProgress(
  batch: RadarBulkBatch,
  lookup: RadarBulkCompletionLookup
): number {
  if (batch.done) return batch.total;
  let finished = 0;
  for (const personId of batch.contactIds) {
    const workflowId = lookup.workflowIdByPersonField[`${personId}:${batch.category}`];
    if (!workflowId) continue;
    const progress = batch.category === 'phone' ? lookup.phone[workflowId] : lookup.email[workflowId];
    if (progress && progress.completedAt !== null) finished += 1;
  }
  return Math.min(batch.total, Math.max(finished, batch.serverCompleted));
}

export const {
  radarBulkRunStarted,
  radarBulkBatchIdAssigned,
  radarBulkBatchSynced,
  radarBulkProgressReceived,
  radarBulkPendingRegistered,
  radarBulkPendingCleared,
  radarBulkWorkflowMapped,
  radarBulkContactsForgotten,
  radarBulkBatchCleared,
  radarBackgroundExportRegistered,
  radarBackgroundExportCleared,
} = radarBulkUnlockSlice.actions;

export default radarBulkUnlockSlice.reducer;
